/**
 * useKeyboardNavigation
 * Handles keyboard navigation for OmniSelect: ArrowUp, ArrowDown, Enter, Escape.
 */
import { useEffect } from 'react'

type IndexUpdater = (index: number | ((prev: number) => number)) => void

export const useKeyboardNavigation = (
    isOpen: null | string,
    options: string[],
    activeIndex: number,
    onOpenChange: (open: null | string) => void,
    onIndexChange: IndexUpdater,
    onSelect: (option: string) => void
): void => {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // closed state: allow open via Enter/Space/ArrowDown
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault()
                    onIndexChange((prev: number) => Math.min(prev + 1, options.length - 1))
                    break
                case 'ArrowUp':
                    e.preventDefault()
                    onIndexChange((prev: number) => Math.max(prev - 1, -1))
                    break
                case 'Enter':
                    e.preventDefault()
                    if (activeIndex >= 0) onSelect(options[activeIndex])
                    break
                case 'Escape':
                    e.preventDefault()
                    onOpenChange(null)
                    onIndexChange(-1)
                    break
                default:
                    break
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isOpen, options, activeIndex, onOpenChange, onIndexChange, onSelect])
}

export default useKeyboardNavigation
